import { userDidAuthenticate } from '~/actions/auth.actions';
import { setPaymentProcessingError } from '~/actions/billing.actions';
import { ActionKeys } from '~/actions/constants';
import { fetchFeatureTests } from '~/actions/feature-tests.actions';
import { pushToGTMDataLayer } from '~/actions/gtm.actions';
import { countOrdersFetch } from '~/actions/orders.actions';
import { fetchUserPlans } from '~/actions/plans.actions';
import { addEventProperties, sendTrackEvent } from '~/actions/prod-analytics.actions';
import { applyOrReplacePromoCode } from '~/actions/promo.actions';
import * as userActions from '~/actions/user.actions';
import { ApplicationState } from '~/store';
import { AxiosError, AxiosResponse } from 'axios';
import { COMPANY_FORM } from '~/constants/form';
import { ID_VERIFICATION_KEY } from '~/constants/query-string-keys';
import { SAVE_BRAND_COLOR_SUCCESS, UPLOAD_BRAND_LOGO_SUCCESS } from '~/constants/user-events';
import * as Cookies from 'js-cookie';
import { NextPaymentModalAction } from '~/reducers/user/user.reducer';
import { setSubmitSucceeded, startSubmit, stopSubmit } from 'redux-form';
import { call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { getPathname } from '~/selectors/router.selectors';
import {
  getCompanySettings,
  getNextModalAction,
  getRequiresEmailVerification,
} from '~/selectors/user.selectors';
import { setupAuthentication } from '~/services/api';
import * as userService from '~/services/user.service';
import * as Asset from '~/types/Asset';
import * as Company from '~/types/Company';
import { IDVerificationStatus } from '~/types/IDVerification';
import { fromJSON, JSONUser, ProfileUser } from '~/types/User';
import { getCookie } from '~/utils/cookies';
import { camelizeKeys } from '~/utils/humps';
import { setCookie } from '~/utils/marketing-source-attribution';
import { Events, track } from '~/utils/segment';
import { getTopLevelDomain } from '~/utils/url';
import { selectState } from '..';

export function getUserJWT() {
  return getCookie('jwt');
}

export function shouldAuthenticate(state: ApplicationState) {
  return !state.user.isAuthenticated;
}

export function* fetchUser() {
  try {
    const jwt: string | undefined = yield call(getUserJWT);
    yield call(setupAuthentication, jwt);
    const response: AxiosResponse<JSONUser> = yield call(userService.getCurrentUser);
    const payload: userActions.FetchUserPayload = {
      user: fromJSON(response.data),
    };

    // User has not yet authenticated, so dispatch userDidAuthenticate
    const willAuthenticate: boolean = yield selectState<boolean>(shouldAuthenticate);
    if (willAuthenticate) {
      yield put(userDidAuthenticate(payload));
    }

    yield put(
      pushToGTMDataLayer({
        group_jwt: response.data.group_jwt,
      }),
    );

    yield put(userActions.fetchUserSuccess(payload));
    yield put(fetchFeatureTests());
    yield put(fetchUserPlans());
    yield put(countOrdersFetch());

    if (!isSameSession()) {
      // tag heap user type on event property
      const properties = {
        'Visit Type': 'Login',
        'User Type': payload.user.hasPurchasedLabel ? 'Return Customer' : 'Return Visitor',
      };
      const hasPurchasedLabelCookie = 'customer';
      setCookie(hasPurchasedLabelCookie, 'true');
      yield put(addEventProperties(properties));
    }

    // Apply promo if pending
    const promocodeCookieValue = Cookies.get('shippo_apply_promo');
    if (promocodeCookieValue) {
      const decodedCookieValue = decodeURIComponent(promocodeCookieValue);
      const cookieDomain = getTopLevelDomain(window.location.href);
      if (payload.user.currentPromo && payload.user.currentPromo.code !== decodedCookieValue) {
        yield put(applyOrReplacePromoCode(decodedCookieValue, true));
      } else if (!payload.user.currentPromo) {
        yield put(applyOrReplacePromoCode(decodedCookieValue, false));
      }
      Cookies.remove('shippo_apply_promo', { domain: `.${cookieDomain}` });
      yield put(userActions.showPromoApplicationNotice());
      yield put(userActions.refreshUser());
    }
  } catch (err) {
    yield put(userActions.fetchUserError());
  }
}

export function* updateCompany(action: ReturnType<typeof userActions.updateCompany>) {
  try {
    yield put(startSubmit(COMPANY_FORM));
    const response: AxiosResponse<Company.JSONCompany> = yield call(
      userService.updateCompany,
      Company.toJSON(action.payload),
    );
    yield put(userActions.updateCompanySuccess(Company.fromJSON(response.data)));
    yield put(stopSubmit(COMPANY_FORM));
    yield put(setSubmitSucceeded(COMPANY_FORM));
  } catch (err) {
    yield put(userActions.updateCompanyError());
    yield put(stopSubmit(COMPANY_FORM));
  }
}

export function* fetchCompany() {
  try {
    const response: AxiosResponse<Company.JSONCompany> = yield call(userService.fetchCompany);
    yield put(userActions.fetchCompanySuccess(Company.fromJSON(response.data)));
  } catch (err) {
    yield put(userActions.fetchCompanyError());
  }
}

export function* refreshUser() {
  try {
    const response: AxiosResponse<JSONUser> = yield call(userService.getCurrentUser);
    const payload: userActions.FetchUserPayload = {
      user: fromJSON(response.data),
    };
    yield put(userActions.fetchUserSuccess(payload));
  } catch (e) {
    yield put(userActions.fetchUserError());
  }
}

export function* revokeAuth() {
  // Same logic as used in django to log a user out
  yield call(Cookies.remove, 'jwt', { domain: CONFIG.shippoRootUrl, path: '/' });
  yield call(Cookies.remove, 'is_logged_in', { domain: CONFIG.shippoRootUrl, path: '/' });
  yield put(userActions.userSetUnauthenticated());
}

export function* updateCreditCard(action: ReturnType<typeof userActions.updateCreditCard>) {
  try {
    const trackerSessionId = getCookie('tracker_sessionid') || '';
    const isImpersonatingCookie = getCookie('is_impersonate') || '';
    yield call(
      userService.updateCreditCard,
      action.payload.tokenId,
      action.payload.billingAddress,
      trackerSessionId,
      isImpersonatingCookie,
    );
    yield put(userActions.refreshUser());
    yield put(userActions.updateCreditCardSuccess());
  } catch (e) {
    const err = e as AxiosError;

    // Generic error in case we cannot get one from the backend
    const error =
      err?.response?.data?.error ||
      "We couldn't save your billing information. Please try again or contact us, we're happy to help you!";

    yield put(setPaymentProcessingError(error || err.toString()));
    yield put(userActions.updateCreditCardError(error));
  }
}

export function* fetchAssets() {
  try {
    const response: AxiosResponse<{ results: Asset.JSONAsset[] }> = yield call(
      userService.fetchAssets,
    );
    const assets = response.data.results.map(Asset.fromJSON);
    yield put(userActions.fetchAssetsSuccess(assets));
  } catch (e) {
    yield put(userActions.fetchAssetsError('unknown_error'));
  }
}

export function* uploadAssetDraft(action: ReturnType<typeof userActions.uploadAssetDraft>) {
  const { assetType, rank } = action.payload;
  try {
    const { asset, file } = action.payload;

    let fullAsset = asset;

    if (!asset) {
      // Create asset if asset no asset is present
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        type: assetType,
        rank: rank,
        imageUrl: '',
        draftImageUrl: '',
        title: null,
        linkUrl: null,
      });

      fullAsset = Asset.fromJSON(result.data);
    }

    yield call(userService.uploadAssetDraft, fullAsset?.objectId!, file);
    yield put(userActions.fetchAssets());
    yield put(userActions.uploadAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err.response!.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.uploadAssetDraftError(assetType, rank, errCode));
  }
}

export function* clearAssetDraft(action: ReturnType<typeof userActions.clearAssetDraft>) {
  const { assetType, asset, rank } = action.payload;
  try {
    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        ...asset,
        type: assetType,
        rank,
      });

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    yield call(userService.clearAssetDraft, fullAsset.objectId);
    yield put(userActions.fetchAssets());
    yield put(userActions.clearAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.clearAssetDraftError(assetType, rank, errCode));
  }
}

export function* revertAssetDraft(action: ReturnType<typeof userActions.clearAssetDraft>) {
  const { assetType, asset, rank } = action.payload;
  try {
    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        ...asset,
        type: assetType,
        rank,
      });

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    // check if there is a live image to revert to in the first place
    yield call(userService.revertAssetDraft, fullAsset.objectId);
    yield put(userActions.fetchAssets());
    yield put(userActions.revertAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.revertAssetDraftError(assetType, rank, errCode));
  }
}

export function* publishAsset(action: ReturnType<typeof userActions.publishAsset>) {
  const { assetType, rank } = action.payload;
  try {
    const { asset } = action.payload;

    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, asset);

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    // If there is a difference between the published and draft images, publish the draft
    if (fullAsset.draftImageUrl !== fullAsset.imageUrl) {
      yield call(userService.publishAssetDraft, fullAsset.objectId);
    }

    // Update asset with new info (title, link, etc)
    yield call(userService.updateAsset, fullAsset);
    yield put(userActions.publishAssetSuccess(assetType, rank));
    yield put(userActions.fetchAssets());
  } catch (e) {
    const err = e as AxiosError;
    let errCode;
    if (err?.response?.status === 400 && err?.response?.data?.link_url) {
      errCode = 'invalid_link';
    } else {
      errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';
    }

    yield put(userActions.publishAssetError(assetType, rank, errCode));
  }
}

export function* uploadAndPublishLogo(action: ReturnType<typeof userActions.uploadAndPublishLogo>) {
  try {
    const { file } = action.payload;

    yield call(userService.uploadAndPublishAsset, Asset.AssetType.LOGO, file);
    yield put(userActions.uploadAndPublishLogoSuccess());
    yield put(sendTrackEvent(UPLOAD_BRAND_LOGO_SUCCESS));
    yield put(userActions.fetchAssets());
  } catch (e) {
    const err = e as AxiosError;

    let errCode = 'unknown_error';

    if (err.response && err.response.status === 400 && err.response?.data.errors) {
      errCode = err.response.data.errors.reduce(
        (acc: string, cur: { code: string }) =>
          acc ? [acc, cur.code].join(';') : (cur.code as string),
        '',
      );
    }

    yield put(userActions.uploadAndPublishLogoError(errCode));
  }
}

export function* updateBrandColors(action: ReturnType<typeof userActions.updateBrandColors>) {
  try {
    // PUT to the /settings/company endpoint requires all fields, but we're only changing brand colors, so populate
    // the fields that aren't changing from the store
    const updatedCompanySettings: Maybe<Company.Company> =
      yield selectState<Maybe<Company.Company>>(getCompanySettings);
    // What happens if this is empty?
    // Try to do it without `!`
    updatedCompanySettings!.brandPrimaryColor = action.payload.brandPrimaryColor;
    updatedCompanySettings!.brandSecondaryColor = action.payload.brandSecondaryColor;

    yield call(userService.updateCompany, Company.toJSON(updatedCompanySettings!));
    yield put(userActions.updateBrandColorsSuccess());
    yield put(sendTrackEvent(SAVE_BRAND_COLOR_SUCCESS));
    yield put(userActions.fetchCompany());
  } catch (e) {
    yield put(userActions.updateBrandColorsError());
  }
}

function isSameSession() {
  const jwt = getUserJWT();
  const copy = getCookie('spare');
  if (!copy || jwt !== copy) {
    setCookie('spare', jwt);
    return false;
  }
  return true;
}

export function* initiateIdVerification() {
  try {
    const currentPathname: string = yield selectState<string>(getPathname);
    const url = new URL(`${currentPathname}?${ID_VERIFICATION_KEY}=true`, CONFIG.reactBaseUrl);
    const returnUrl = url.toString();
    const result: AxiosResponse<{ redirect_url: string }> = yield call(
      userService.createVerificationIntent,
      returnUrl,
    );
    yield call([window.location, window.location.assign], result.data.redirect_url);
  } catch (err) {
    // I'm going to cheat.
    // Instead of saving error state somewhere I'm going to use
    // the id verification state to set an error with a fake IDVerification.
    // So if any error happens we can display the verification
    // failed modal with a generic message that the user can retry.
    yield put(
      userActions.setIdVerificationStatus({
        state: IDVerificationStatus.VERIFICATION_FAILED_ALLOW_RETRY,
        errors: ['error_processing_request'],
      }),
    );
  }
}

export function* updateUser(action: ReturnType<typeof userActions.updateUser>) {
  try {
    const isImpersonatingCookie = getCookie('is_impersonate') || '';
    const response: AxiosResponse<JSONUser> = yield call(
      userService.updateUser,
      action.payload.user,
      isImpersonatingCookie,
    );
    yield put(userActions.updateUserSuccess(camelizeKeys(response.data) as ProfileUser));
    yield put(userActions.updateUserError('success'));
    yield call(track, Events.update_user_information);
  } catch (e) {
    const err = e as AxiosError;
    const userError = err.response?.data?.errors
      ? err.response?.data?.errors?.join('. ')
      : 'An error occurred while updating your profile. Please try again later.';
    yield put(userActions.updateUserError(userError));
  }
}

export function* addUser(action: ReturnType<typeof userActions.addUser>) {
  try {
    const response: AxiosResponse<JSONUser> = yield call(userService.addUser, action.payload.email);
    action.callback(camelizeKeys(response.data) as ProfileUser);
  } catch (e) {
    const err = e as AxiosError;
    action.callback({ err: err?.response?.data ?? 'unknown_error' });
  }
}

export function* sendInvite(action: ReturnType<typeof userActions.resendInvite>) {
  try {
    const response: AxiosResponse<{ data: unknown }> = yield call(
      userService.sendInvite,
      action.payload.userId,
    );
    action.callback(camelizeKeys(response.data));
  } catch (e) {
    const err = e as AxiosError;
    action.callback({ err: err?.response?.data || 'unknown_error' });
  }
}

export function* deleteUser(action: ReturnType<typeof userActions.resendInvite>) {
  try {
    yield call(userService.deleteUser, action.payload.userId);
    action.callback({ success: true });
  } catch (err) {
    action.callback({ success: false });
  }
}

export function* getLoginUsers(action: ReturnType<typeof userActions.getLoginUsers>) {
  try {
    const response: AxiosResponse<{ data: unknown }> = yield call(userService.getLoginUsers);
    action.callback(camelizeKeys(response.data));
  } catch (err) {
    action.callback({ err });
  }
}

export function* maybeShowEmailVerificationModal(
  action: ReturnType<typeof userActions.maybeShowEmailVerificationModal>,
) {
  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  if (requiresEmailVerification) {
    yield put(userActions.showEmailVerificationModal(action.payload.nextModalAction));
  } else if (action.payload.nextModalAction) {
    yield put(action.payload.nextModalAction);
  }
}

export function* checkRequiresEmailVerification(
  action: ReturnType<typeof userActions.checkRequiresEmailVerification>,
) {
  yield put(userActions.refreshUser());

  // Wait for refreshUser() to trigger billing refresh and succeed or fail
  yield take([
    ActionKeys.FETCH_PAYMENT_METHODS_SUCCESS,
    ActionKeys.FETCH_PAYMENT_METHODS_ERROR,
    ActionKeys.USER_FETCH_ERROR,
  ]);

  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  if (!requiresEmailVerification) {
    // Must come first, verifySuccessCallback() clears nextModalAction state
    const nextModalAction: Maybe<NextPaymentModalAction> =
      yield selectState<Maybe<NextPaymentModalAction>>(getNextModalAction);

    yield put(userActions.emailVerificationSuccess());
    yield call(action.payload.verifySuccessCallback);

    if (nextModalAction) {
      yield put(nextModalAction);
    }
  }
}

export default [
  takeLatest(ActionKeys.USER_FETCH, fetchUser),
  takeLatest(ActionKeys.USER_ADD, addUser),
  takeLatest(ActionKeys.COMPANY_UPDATE, updateCompany),
  takeLatest(ActionKeys.COMPANY_FETCH, fetchCompany),
  takeLatest(ActionKeys.USER_REFRESH, refreshUser),
  takeLatest(ActionKeys.REVOKE_AUTH, revokeAuth),
  takeLatest(ActionKeys.USER_UPDATE_CREDIT_CARD, updateCreditCard),
  takeLatest(ActionKeys.USER_ASSETS_FETCH, fetchAssets),
  takeEvery(ActionKeys.USER_ASSET_UPLOAD_DRAFT, uploadAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_CLEAR_DRAFT, clearAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_REVERT_DRAFT, revertAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_PUBLISH, publishAsset),
  takeLatest(ActionKeys.USER_INITIATE_ID_VERIFICATION, initiateIdVerification),
  takeLatest(ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO, uploadAndPublishLogo),
  takeLatest(ActionKeys.USER_BRAND_UPDATE_COLORS, updateBrandColors),
  takeLatest(ActionKeys.USER_UPDATE, updateUser),
  takeLatest(ActionKeys.USER_ALL_FETCH, getLoginUsers),
  takeLatest(ActionKeys.USER_RESEND_INVITE, sendInvite),
  takeLatest(ActionKeys.USER_DELETE, deleteUser),
  takeLatest(ActionKeys.MAYBE_SHOW_EMAIL_VERIFICATION_MODAL, maybeShowEmailVerificationModal),
  takeLatest(ActionKeys.CHECK_REQUIRES_EMAIL_VERIFICATION, checkRequiresEmailVerification),
];
